export const formatDateOnly = (date) => {
  if (!date) return '';
  try {
    const parts = date.split('-');
    if (parts.length === 3) {
      const localDate = new Date(parts[0], parts[1] - 1, parts[2]);
      return localDate.toLocaleDateString('en-IN', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
    return date;
  } catch (error) {
    return date;
  }
};


// Format time display
export const formatTimeOnly = (time) => {
  if (!time) return '';
  try {
    const [hours, minutes] = time.split(':');
    const date = new Date(2000, 0, 1, parseInt(hours, 10), parseInt(minutes, 10));
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return time;
  }
};

// Calculate duration information
export const calculateDuration = (bookingData) => {
  if (!bookingData.startDate || !bookingData.endDate || !bookingData.pickupTime || !bookingData.dropoffTime) {
    return null;
  }

  try {
    const start = new Date(`${bookingData.startDate}T${bookingData.pickupTime}`);
    const end = new Date(`${bookingData.endDate}T${bookingData.dropoffTime}`);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.error('Invalid datetime format');
      return null;
    }

    let hours = (end - start) / (1000 * 60 * 60);

    if (hours <= 0) {
      if (bookingData.startDate === bookingData.endDate) {
        const [startHour, startMin] = bookingData.pickupTime.split(':').map(Number);
        const [endHour, endMin] = bookingData.dropoffTime.split(':').map(Number);

        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;

        if (endMinutes <= startMinutes) {
          hours = (24 * 60 - startMinutes + endMinutes) / 60;
        } else {
          hours = (endMinutes - startMinutes) / 60;
        }
      }

      if (hours <= 0) {
        console.error('Invalid duration - end time must be after start time');
        return null;
      }
    }

    const days = Math.floor(hours / 24);
    const remainingHours = Math.floor(hours % 24);

    return {
      totalHours: hours,
      days,
      remainingHours,
      bookingType: hours <= 6 ? 'HOURLY' : hours <= 144 ? 'DAILY' : 'WEEKLY',
      displayDuration: days > 0
        ? `${days} day${days > 1 ? 's' : ''}${remainingHours > 0 ? ` ${remainingHours}h` : ''}`
        : `${hours.toFixed(1)} hours`
    };

  } catch (error) {
    console.error('Error calculating duration:', error);
    return null;
  }
};

// Calculate estimated price
export const calculateEstimatedPrice = (bookingData, durationInfo, priceRates) => {
  if (!durationInfo || !priceRates) {
    return null;
  }

  try {
    if (bookingData.hasPackageSelected && bookingData.packagePrice > 0) {
      console.log("Using package pricing:", bookingData.packagePrice);
      return {
        subtotal: bookingData.packagePrice,
        isPackagePrice: true,
        packageName: bookingData.packageName,
        totalHours: durationInfo.totalHours,
        bookingType: bookingData.bookingType
      };
    }

    let subtotal;
    const hours = durationInfo.totalHours;

    if (hours < 24) {
      if (hours > 6) {
        subtotal = priceRates.dailyRate;
        console.log("Hourly pricing (>6h, bill as 1 day):", { hours, rate: priceRates.dailyRate, subtotal });
      } else {
        subtotal = Math.ceil(hours) * priceRates.hourlyRate;
        console.log("Hourly pricing:", { hours, rate: priceRates.hourlyRate, subtotal });
      }
    } else {
      const fullDays = Math.floor(hours / 24);
      const remainder = hours - (fullDays * 24);

      console.log("Duration breakdown:", { hours, fullDays, remainder });

      if (remainder === 0) {
        subtotal = fullDays * priceRates.dailyRate;
        console.log("Exact days:", { fullDays, subtotal });
      } else if (remainder <= 6) {
        const baseCost = fullDays * priceRates.dailyRate;
        const remainderCost = Math.ceil(remainder) * priceRates.hourlyRate;
        subtotal = baseCost + remainderCost;
        console.log("Days + hourly remainder:", { fullDays, remainder, baseCost, remainderCost, subtotal });
      } else {
        subtotal = (fullDays + 1) * priceRates.dailyRate;
        console.log("Rounded up to full day:", { fullDays: fullDays + 1, subtotal });
      }
    }

    return {
      subtotal,
      hourlyRate: priceRates.hourlyRate,
      dailyRate: priceRates.dailyRate,
      isPackagePrice: false,
      totalHours: durationInfo.totalHours,
      bookingType: durationInfo.bookingType
    };
  } catch (error) {
    console.error("Error calculating price:", error);
    return null;
  }
};

// Calculate final totals
// Security deposit is no longer collected — total equals the payable amount.
export const calculateTotals = (estimatedPrice, couponDiscount, gstRate) => {
  if (!estimatedPrice) {
    return {
      subtotal: 0,
      gst: 0,
      couponDiscount: 0,
      deposit: 0,
      payableAmount: 0,
      total: 0
    };
  }

  const subtotal = Math.round(estimatedPrice.subtotal || 0);
  const gst = Math.round(subtotal * gstRate);
  const payableAmount = Math.max(0, subtotal + gst - couponDiscount);

  console.log('💰 Price Calculation (RUPEES):');
  console.log('  Subtotal: ₹' + subtotal);
  console.log('  GST (5%): ₹' + gst);
  console.log('  Discount: ₹' + couponDiscount);
  console.log('  TOTAL: ₹' + payableAmount);

  return { subtotal, gst, couponDiscount, deposit: 0, payableAmount, total: payableAmount };
};

// Prepare booking request
export const prepareBookingRequest = (bookingData, totals, durationInfo, paymentMethod, appliedCoupon) => {
  if (!totals || totals.total <= 0) {
    throw new Error('Invalid price calculation');
  }

  console.log('📋 Preparing booking request (amounts in RUPEES):');

  const request = {
    vehicleId: parseInt(bookingData.bikeId, 10),
    startDate: bookingData.startDate,
    endDate: bookingData.endDate,
    pickupTime: bookingData.pickupTime,
    dropoffTime: bookingData.dropoffTime,

    charges: totals.subtotal || 0,
    gst: totals.gst || 0,
    advanceAmount: totals.deposit || 0,
    totalCharges: totals.payableAmount || 0,
    finalAmount: totals.total || 0,

    couponCode: appliedCoupon?.code || null,
    couponAmount: appliedCoupon?.discount || 0,

    paymentType: paymentMethod === 'online' ? 2 : 1,
    paymentStatus: paymentMethod === 'online' ? 'INITIATED' : 'PENDING',

    addressType: bookingData.pickupMode === 'delivery' ? 'Delivery' : 'Self Pickup',
    address: bookingData.deliveryAddress || `${bookingData.storeName}, ${bookingData.city}`,

    bookingStatus: 1,
    totalHours: durationInfo?.totalHours || 0
  };

  console.log('📤 Request payload:', JSON.stringify(request, null, 2));

  if (!request.charges || request.charges <= 0) {
    throw new Error('Invalid charges amount');
  }
  if (!request.finalAmount || request.finalAmount <= 0) {
    throw new Error('Invalid final amount');
  }

  return request;
};
